import React from 'react';
import { AppTable, Button, ProgressBar, Tooltip } from '@allygpo/ally-ux';
import { getCoreRowModel, getExpandedRowModel, useReactTable, type ColumnDef, type ExpandedState, type SortingState, type Table } from '@tanstack/react-table';
import { Checkbox, MixBar, alignEnd, familyLabel, headerEnd, tableView } from '../../components/ui';
import type { SortDir } from '../../components/ui';
import type { ScenarioResult, ScenarioSummary } from '../../types';
import { money, signedMoney } from '../../utils/format';

export type GroupedScenarioResult = {
  scenario_id: number;
  summary?: ScenarioSummary;
  scenarioKind?: string;
  payerAssignments?: Record<string, string>;
  parentScenarioId?: number;
  totalNCR: number;
  totalWeightedNCR?: number;
  totalRebate: number;
  totalMedicareReimb: number;
  totalPvtReimb: number;
  totalPrice: number;
  distributionBreakdown: { family: string; color: string; percent: number }[];
  details: ScenarioResult[];
};

export type ScenarioSortKey = 'name' | 'reimb' | 'price' | 'rebate' | 'ncr' | 'delta';
const SORT_KEYS: readonly string[] = ['name', 'reimb', 'price', 'rebate', 'ncr', 'delta'];

/** Rows per page: the default, then the paginator's choices. */
export const SCENARIO_PAGE_SIZE = 10;
const ROWS_PER_PAGE_OPTIONS = [5, SCENARIO_PAGE_SIZE, 25];

/** What the scenario cells read from `meta.view`. */
interface ScenarioView {
  metricName: string;
  metricReimb: (g: GroupedScenarioResult) => number;
  metricNcr: (g: GroupedScenarioResult) => number;
  /** Current's NCR, for "vs current"; null when there's no Current row. */
  currentNcr: number | null;
  /** Largest |NCR| in the area, the full width of the NCR bars. */
  maxAbsNcr: number;
  labelOf: (id: number) => string;
  badgeFor: (id: number) => React.ReactNode;
  isFavorite: (g: GroupedScenarioResult) => boolean;
  onToggleFavorite: (g: GroupedScenarioResult) => void;
  isDemoMode: boolean;
  isSelected: (id: number) => boolean;
  onToggleSelected: (id: number) => void;
}

const view = (table: Table<GroupedScenarioResult>) => tableView<ScenarioView, GroupedScenarioResult>(table);
const deltaOf = (v: ScenarioView, g: GroupedScenarioResult) => (g.scenario_id === 0 || v.currentNcr === null ? null : v.metricNcr(g) - v.currentNcr);
// TanStack only offers sorting on columns with an accessor. The page sorts (manualSorting), so the value is unused.
const sortable = { accessorFn: (g: GroupedScenarioResult) => g.scenario_id };
// AppTable sets the table's width to the sum of the column sizes, so these are minimums that fit a laptop
// (about 700px of card at 1024 wide); the auto layout spreads the rest. Long numeric headers may wrap.
const NUM_SIZE = 72;
const WRAP_HEADER = 'white-space-normal';
const moneyColumn = (id: ScenarioSortKey, header: (v: ScenarioView) => string, value: (v: ScenarioView, g: GroupedScenarioResult) => number): ColumnDef<GroupedScenarioResult> => ({
  id,
  ...sortable,
  size: NUM_SIZE,
  header: ({ table }) => headerEnd(header(view(table))),
  meta: { ...alignEnd, headerClassName: WRAP_HEADER, cellClassName: 'text-nowrap' },
  cell: ({ row: { original: g }, table }) => money(value(view(table), g)),
});

const columns: ColumnDef<GroupedScenarioResult>[] = [
  {
    id: 'controls',
    size: 104,
    enableSorting: false,
    header: '',
    cell: ({ row, table }) => {
      const g = row.original;
      const v = view(table);
      const id = g.scenario_id;
      const label = v.labelOf(id);
      const fav = v.isFavorite(g);
      const open = row.getIsExpanded();
      // Per-row class so each Tooltip binds to its own star rather than to every star in the table.
      const demoTipClass = `favorite-star-demo-${id}`;
      return (
        <div className="flex align-items-center">
          {v.isDemoMode && <Tooltip target={`.${demoTipClass}`} position="top" />}
          <Button
            variant="secondary-text"
            className={`results-star${fav ? ' text-warning' : ''}${v.isDemoMode ? ` opacity-50 ${demoTipClass}` : ''}`}
            aria-label={fav ? `Remove ${label} from favorites` : `Add ${label} to favorites`}
            aria-pressed={fav}
            data-pr-tooltip={v.isDemoMode ? 'Read-only demo account' : undefined}
            onClick={() => { if (!v.isDemoMode) v.onToggleFavorite(g); }}
          >
            <i className={`pi ${fav ? 'pi-star-fill' : 'pi-star'}`} aria-hidden="true" />
          </Button>
          {/* Current is always part of a comparison, so it has nothing to check. Keeps its slot so columns line up. */}
          <span className="flex align-items-center justify-content-center w-2rem">
            {id !== 0 && <Checkbox checked={v.isSelected(id)} onChange={() => v.onToggleSelected(id)} ariaLabel={`Compare ${label} with current`} />}
          </span>
          <Button variant="secondary-text" className="results-expander" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`} onClick={row.getToggleExpandedHandler()}>
            <i className={`pi ${open ? 'pi-chevron-down' : 'pi-chevron-right'}`} aria-hidden="true" />
          </Button>
        </div>
      );
    },
  },
  {
    id: 'name',
    ...sortable,
    size: 110,
    header: () => (
      <span className="inline-flex align-items-center u-gap-1">
        Scenario
        <i className="pi pi-info-circle results-info-icon text-tertiary text-xs" data-pr-tooltip="Rankings are for scenario comparison only." data-pr-position="top" />
      </span>
    ),
    cell: ({ row: { original: g }, table }) => {
      const v = view(table);
      const id = g.scenario_id;
      return (
        // The id is where "View breakdown" on the highest-NCR strip scrolls to.
        <span id={`scenario-row-${id}`} className="flex flex-column">
          <span className="results-name-line flex align-items-center u-gap-2 flex-wrap font-medium">{v.labelOf(id)}{v.badgeFor(id)}</span>
          {g.parentScenarioId != null && <span className="text-2xs text-secondary">from {v.labelOf(g.parentScenarioId)}</span>}
        </span>
      );
    },
  },
  {
    id: 'mix',
    size: 80,
    enableSorting: false,
    header: 'Mix',
    cell: ({ row: { original: g } }) => {
      const visible = g.distributionBreakdown.filter((d) => d.percent > 0);
      return (
        <span className="flex align-items-center">
          <Tooltip target={`.dist-bar-${g.scenario_id}`} position="top" mouseTrack mouseTrackTop={10}>
            <div className="flex flex-column u-gap-1 min-w-9rem">
              {visible.map((d) => (
                <div key={d.family} className="flex align-items-center u-gap-1">
                  <span className="siq-swatch" style={{ background: d.color }} />
                  <span className="flex-1">{familyLabel(d.family)}</span>
                  <span className="font-semibold u-ml-2">{Math.round(d.percent * 100)}%</span>
                </div>
              ))}
            </div>
          </Tooltip>
          <MixBar className={`dist-bar-${g.scenario_id}`} segments={g.distributionBreakdown.map((d) => ({ name: d.family, share: d.percent, color: d.color }))} />
        </span>
      );
    },
  },
  moneyColumn('reimb', (v) => `${v.metricName} reimb.`, (v, g) => v.metricReimb(g)),
  moneyColumn('price', () => 'Price', (_v, g) => g.totalPrice),
  moneyColumn('rebate', () => 'Rebate', (_v, g) => g.totalRebate),
  {
    id: 'ncr',
    ...sortable,
    size: 80,
    header: ({ table }) => headerEnd(`${view(table).metricName} NCR`),
    meta: { ...alignEnd, headerClassName: WRAP_HEADER },
    cell: ({ row: { original: g }, table }) => {
      const v = view(table);
      const ncr = v.metricNcr(g);
      const width = v.maxAbsNcr > 0 ? (Math.abs(ncr) / v.maxAbsNcr) * 100 : 0;
      const color = ncr < 0 ? 'var(--siq-ncr-bar-negative)' : g.scenario_id === 0 ? 'var(--siq-icon-faint)' : 'var(--siq-ncr-bar)';
      return (
        <span className="inline-flex flex-column align-items-end u-gap-1 text-nowrap">
          <span className={`font-semibold${ncr < 0 ? ' text-danger' : ''}`}>{money(ncr)}</span>
          <ProgressBar value={width} color={color} className="w-4rem" aria-hidden="true" />
        </span>
      );
    },
  },
  {
    id: 'delta',
    ...sortable,
    size: NUM_SIZE,
    header: () => headerEnd('vs current'),
    meta: { ...alignEnd, headerClassName: WRAP_HEADER },
    cell: ({ row: { original: g }, table }) => {
      const delta = deltaOf(view(table), g);
      if (delta === null) return <span className="siq-dim text-tertiary">—</span>;
      return <span className={`font-medium text-nowrap${delta > 0 ? ' siq-pos text-success' : delta < 0 ? ' text-danger' : ''}`}>{signedMoney(delta)}</span>;
    },
  },
];

interface ScenarioTableProps extends ScenarioView {
  /** Current, Proposed and alternates: on every page, outside sorting and paging. */
  pinnedRows: GroupedScenarioResult[];
  /** This page of the sorted, generated scenarios. */
  pageRows: GroupedScenarioResult[];
  /** Generated scenarios across all pages (pinned rows not counted). */
  total: number;
  page: number;
  pageSize: number;
  onPagination: (page: number, pageSize: number) => void;
  sortKey: ScenarioSortKey;
  sortDir: SortDir;
  onSort: (key: ScenarioSortKey) => void;
  expanded: Record<string, boolean>;
  onToggleExpanded: (id: number) => void;
  renderExpansion: (g: GroupedScenarioResult) => React.ReactNode;
}

/**
 * Results' scenario table. Sorting and paging are the page's (pinned rows stay on top of every page,
 * which TanStack's own sorting can't do), so the table is manual and only presents them.
 */
const ScenarioTable: React.FC<ScenarioTableProps> = ({
  pinnedRows, pageRows, total, page, pageSize, onPagination, sortKey, sortDir, onSort, expanded, onToggleExpanded, renderExpansion, ...cellView
}) => {
  const sorting: SortingState = [{ id: sortKey, desc: sortDir === 'desc' }];
  const pagination = { pageIndex: page, pageSize };
  const table = useReactTable({
    data: [...pinnedRows, ...pageRows],
    columns,
    meta: { view: cellView },
    state: { sorting, pagination, expanded: expanded as ExpandedState },
    manualSorting: true,
    manualPagination: true,
    rowCount: total,
    enableSortingRemoval: false,
    autoResetPageIndex: false,
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      // A click on the active column comes back as the same id; onSort flips it. Any other id is a new sort.
      const id = next[0]?.id ?? sortKey;
      if (SORT_KEYS.includes(id)) onSort(id as ScenarioSortKey);
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      onPagination(next.pageIndex, next.pageSize);
    },
    onExpandedChange: (updater) => {
      const next = typeof updater === 'function' ? updater(expanded as ExpandedState) : updater;
      if (next === true) return;
      // One toggle at a time: find the row whose state changed.
      const ids = new Set([...Object.keys(expanded), ...Object.keys(next)]);
      ids.forEach((id) => { if (!!expanded[id] !== !!(next as Record<string, boolean>)[id]) onToggleExpanded(Number(id)); });
    },
    getRowId: (g) => String(g.scenario_id),
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });
  return (
    <AppTable
      isBordered={false}
      size="sm"
      ariaLabel="Scenarios"
      table={table}
      tableLayout="auto"
      emptyState={<span>No results found</span>}
      rowClassName={({ rowData }) => (cellView.isSelected(rowData.scenario_id) ? 'bg-surface-selected' : '')}
      onCellClick={({ row, columnId }) => { if (columnId !== 'controls') row.toggleExpanded(); }}
      rowExpansion={{ renderSubRow: (row) => <div className="results-expansion ">{renderExpansion(row.original)}</div> }}
      pagination={total > 0 ? { rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS } : false}
      messages={{ paginationRangeText: ({ first, last, total: count }) => `Showing ${first}–${last} of ${count} scenarios` }}
    />
  );
};

export default ScenarioTable;
